/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient } from '@/lib/api';
import type { User } from '@/lib/types';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ user: User } | User>('/auth/me');
      // Backend returns { user: ... } envelope (MeResponse). Older shape was
      // a flat User; accept either.
      const data = res.data as { user?: User };
      setUser(data && data.user ? data.user : (res.data as User));
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } } | undefined)?.response?.status;
      if (status === 401) {
        setUser(null);
      } else {
        setError((e as Error).message ?? 'Failed to load session');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // ignore — clear local state regardless
    }
    setUser(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, refresh, signOut }),
    [user, loading, error, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
